import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { CreditCard, Landmark, Receipt, RefreshCw } from 'lucide-react';
import { useLocale, useT } from '../../i18n';
import { isStandaloneMobile } from '../../lib/nativeBridge';
import {
  createPaymentAccountSession,
  openPaymentAccount,
  readPaymentAccount,
  type PaymentAccountRead,
} from '../../lib/api/paymentAccount';
import { SetupCard } from '../ui';
import type { PaymentAccountPanelMode } from './PaymentAccountEmbedded';

// ─────────────────────────────────────────────────────────────────────
// «BETALING» IN THE ORGANIZATION'S SETTINGS (#946).
//
// The owner opens the organization's Stripe account and completes Stripe's
// onboarding here, with Stripe's embedded components. Decided 2026-10-18:
// embedded onboarding, no Stripe Dashboard for the organization, Stripe liable
// and collecting the requirements — so Stripe asks the owner to sign in to
// Stripe in a popup, and the screens are Stripe's.
//
// WHAT THIS SCREEN DOES NOT DECIDE:
//
//   • WHO MAY. The server answers 403 for anyone but the owner, and the
//     section says so rather than hiding: an administrator wondering why
//     payments are not set up should learn who can do it.
//   • WHETHER IT IS READY. Every status shown comes from Stripe through the
//     server; leaving the onboarding form only triggers a re-read.
//
// BROWSER ONLY: Stripe's embedded components cannot run in an app's web view,
// so inside the native shell the section explains where to go instead.
//
// PAYOUTS AND PAYMENTS (phase 2): with no Stripe Dashboard, this is where the
// organization sees its payouts and answers disputes. They are offered only
// once the details are SUBMITTED — before that there is nothing to pay out or
// dispute, and Stripe's onboarding is what the owner needs, so that is what is
// offered. After it they stay available even while a new requirement is due:
// a dispute has a deadline that does not wait for paperwork.
// ─────────────────────────────────────────────────────────────────────

const PaymentAccountEmbedded = lazy(() => import('./PaymentAccountEmbedded'));

/** The build's publishable key — the same one the payment sheet uses. */
function publishableKey(): string {
  return (window.__VITE_STRIPE_PUBLISHABLE_KEY__ ?? '').trim();
}

export function PaymentAccountSection() {
  const t = useT();
  const { locale } = useLocale();
  const [read, setRead] = useState<PaymentAccountRead | null>(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [panel, setPanel] = useState<{ mode: PaymentAccountPanelMode; secret: string } | null>(
    null,
  );

  const refresh = useCallback(async () => {
    setRead(await readPaymentAccount());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** A sentence for an API code. Codes are never shown raw except as a reference. */
  const sentence = (c: string): string => {
    if (c === 'forbidden') return t('paymentAccount.ownerOnly');
    if (c === 'not_available') return t('paymentAccount.notAvailable');
    if (c === 'unreachable') return t('paymentAccount.unreachable');
    if (c === 'provider_unavailable') return t('paymentAccount.providerUnavailable');
    if (c === 'provider_refused') return t('paymentAccount.providerRefused', { code: c });
    if (c === 'reauthentication_required') return t('paymentAccount.reauth');
    if (c === 'payment_account_ambiguous') return t('paymentAccount.ambiguous');
    if (c === 'opening_in_progress') return t('paymentAccount.inProgress');
    return t('paymentAccount.error', { code: c });
  };

  const startPanel = async (mode: PaymentAccountPanelMode) => {
    setBusy(true);
    setCode(null);
    const session = await createPaymentAccountSession();
    setBusy(false);
    if (session.code || !session.clientSecret) {
      setCode(session.code ?? 'invalid_response');
      return;
    }
    setPanel({ mode, secret: session.clientSecret });
  };

  const open = async () => {
    setBusy(true);
    setCode(null);
    const result = await openPaymentAccount();
    setBusy(false);
    if (result.code) {
      setCode(result.code);
      return;
    }
    await refresh();
    await startPanel('onboarding');
  };

  const closePanel = () => {
    setPanel(null);
    void refresh();
  };

  let body: JSX.Element;
  if (isStandaloneMobile()) {
    body = <p className="text-sm text-ink-2">{t('paymentAccount.native')}</p>;
  } else if (read === null) {
    body = (
      <p className="text-sm text-ink-3" role="status">
        {t('paymentAccount.loading')}
      </p>
    );
  } else if (read.code) {
    body = <p className="text-sm text-ink-2">{sentence(read.code)}</p>;
  } else if (!publishableKey()) {
    body = <p className="text-sm text-ink-2">{t('paymentAccount.noKey')}</p>;
  } else if (panel) {
    body = (
      <div className="space-y-3">
        {panel.mode === 'payouts' ? (
          <p className="text-sm text-ink-2">{t('paymentAccount.payouts.lead')}</p>
        ) : panel.mode === 'payments' ? (
          <p className="text-sm text-ink-2">{t('paymentAccount.payments.lead')}</p>
        ) : null}
        <p className="text-[12px] text-ink-3">{t('paymentAccount.stripeSignIn')}</p>
        <Suspense
          fallback={
            <p className="text-sm text-ink-3" role="status">
              {t('paymentAccount.loadingStripe')}
            </p>
          }
        >
          <PaymentAccountEmbedded
            mode={panel.mode}
            firstSecret={panel.secret}
            publishableKey={publishableKey()}
            locale={locale === 'en' ? 'en-GB' : 'nb-NO'}
            onExit={closePanel}
          />
        </Suspense>
        <div className="flex justify-end">
          <button type="button" className="btn-outline text-sm" onClick={closePanel}>
            {t('paymentAccount.close')}
          </button>
        </div>
      </div>
    );
  } else if (!read.connected) {
    body = (
      <div className="space-y-3">
        {/* AN UNFINISHED ATTEMPT (#948): Stripe may hold an account we never
            heard about. The same button finishes it; the server never opens a
            second one. */}
        <p className="text-sm text-ink-2">
          {read.opening ? t('paymentAccount.opening') : t('paymentAccount.notConnected')}
        </p>
        <button
          type="button"
          className="btn-lime text-sm"
          onClick={() => void open()}
          disabled={busy}
        >
          <CreditCard size={14} aria-hidden="true" />{' '}
          {busy
            ? t('paymentAccount.working')
            : read.opening
              ? t('paymentAccount.finish')
              : t('paymentAccount.open')}
        </button>
      </div>
    );
  } else {
    const s = read.status;
    const ready = !!s && s.chargesEnabled && s.currentlyDue === 0 && s.pastDue === 0;
    const needsOnboarding = !s || !s.detailsSubmitted || s.currentlyDue > 0 || s.pastDue > 0;
    const onboarded = !!s && s.detailsSubmitted;
    body = (
      <div className="space-y-3">
        {s ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-ink-3">{t('paymentAccount.status.charges')}</dt>
            <dd>
              {s.chargesEnabled ? t('paymentAccount.status.yes') : t('paymentAccount.status.no')}
            </dd>
            <dt className="text-ink-3">{t('paymentAccount.status.payouts')}</dt>
            <dd>
              {s.payoutsEnabled ? t('paymentAccount.status.yes') : t('paymentAccount.status.no')}
            </dd>
          </dl>
        ) : null}
        {s && s.pastDue > 0 ? (
          <p className="text-sm text-danger" role="alert">
            {t('paymentAccount.status.pastDue', { count: s.pastDue })}
          </p>
        ) : s && s.currentlyDue > 0 ? (
          <p className="text-sm text-ink-2">
            {t('paymentAccount.status.due', { count: s.currentlyDue })}
          </p>
        ) : s && s.disabledReason === 'requirements.pending_verification' ? (
          <p className="text-sm text-ink-2">{t('paymentAccount.status.pending')}</p>
        ) : ready ? (
          <p className="text-sm text-ink-2">{t('paymentAccount.status.ready')}</p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={needsOnboarding ? 'btn-lime text-sm' : 'btn-outline text-sm'}
            onClick={() => void startPanel(needsOnboarding ? 'onboarding' : 'manage')}
            disabled={busy}
          >
            {busy
              ? t('paymentAccount.working')
              : needsOnboarding
                ? t('paymentAccount.continue')
                : t('paymentAccount.manage')}
          </button>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => void refresh()}
            disabled={busy}
          >
            <RefreshCw size={12} aria-hidden="true" /> {t('paymentAccount.refresh')}
          </button>
        </div>
        {onboarded ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-outline text-sm"
              onClick={() => void startPanel('payouts')}
              disabled={busy}
            >
              <Landmark size={14} aria-hidden="true" /> {t('paymentAccount.payouts.open')}
            </button>
            <button
              type="button"
              className="btn-outline text-sm"
              onClick={() => void startPanel('payments')}
              disabled={busy}
            >
              <Receipt size={14} aria-hidden="true" /> {t('paymentAccount.payments.open')}
            </button>
          </div>
        ) : (
          <p className="text-sm text-ink-2">{t('paymentAccount.afterSetup')}</p>
        )}
      </div>
    );
  }

  return (
    <SetupCard label={t('paymentAccount.title')}>
      <div className="space-y-3">
        <p className="text-[12px] text-ink-3">{t('paymentAccount.lead')}</p>
        {body}
        {code ? (
          <p className="text-sm text-danger" role="alert">
            {sentence(code)}
          </p>
        ) : null}
      </div>
    </SetupCard>
  );
}
